import { useEffect, useMemo, useState } from "react";
import { useAmbienteMedicao } from "../contexts/AmbienteMedicaoContext";
import NavBar from "../Componentes/NavBar";
import styled, { keyframes } from "styled-components";
import { BsArrowRight } from "react-icons/bs";
import { ImSpinner8 } from "react-icons/im";
import { useNavigate } from "react-router-dom";
import { useModal } from "../contexts/ModalContext";
import SideMenu from "../Componentes/SideMenu";
import Overlay from "../Componentes/Overlay";
import SideMenuBtn from "../Componentes/SideMenuBtn";
import ModalNovoAmbiente from "../Modulos/ControleMedicao/PedidosCompra/ModalNovoAmbiente";
import ModalGerenciarObras from "../Modulos/ControleMedicao/PedidosCompra/ModalGerenciarObras";
import ModalNfPendentesBM from "../Modulos/ControleMedicao/PedidosCompra/ModalNfPendentesBM";
import useApi from "../hooks/useApi";

const LogoSlot = styled.div`
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    border-radius: 10px;
    overflow: hidden;
    background-color: #f3f4f6;
    border: 1px solid ${(props) => (props.$active ? "#007BFF" : "#e5e7eb")};
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
`;

const ThumbPlaceholderText = styled.span`
    width: 100%;
    display: block;
    font-size: 10px;
    color: #9ca3af;
    text-align: center;
    padding: 4px;
    line-height: 1.25;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

function ObraLogoThumb({ logoUrl, active }) {
    const url = logoUrl != null && String(logoUrl).trim() !== "" ? String(logoUrl).trim() : null;
    const [broken, setBroken] = useState(false);

    useEffect(() => {
        setBroken(false);
    }, [url]);

    if (!url || broken) {
        return (
            <LogoSlot $active={active} title="Sem imagem">
                <ThumbPlaceholderText>SEM IMAGEM</ThumbPlaceholderText>
            </LogoSlot>
        );
    }

    return (
        <LogoSlot $active={active} title="Logo da obra">
            <img
                src={url}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                onError={() => setBroken(true)}
            />
        </LogoSlot>
    );
}

const Container = styled.div`
    height: 100dvh;
    width: 100%;
    background-color: #242d34;
    display: flex;
    flex-direction: column;
`
const SelectorWrapper = styled.div`
    width: min(500px, calc(100vw - 32px));
    max-height: calc(100dvh - 120px);
    background-color: #fff;
    border-radius: 15px;
    margin: auto;
    display: flex;
    flex-direction: column;
    padding: 30px;
    box-sizing: border-box;
    overflow: hidden;
    & h2 {
        margin: 0;
    }
    @media (max-width: 600px) {
        width: calc(100vw - 20px);
        max-height: calc(100dvh - 90px);
        padding: 20px;
    }
`
const Separator = styled.div`
    width: 100%;
    height: 1px;
    background-color: #242d34;
    margin: 20px 0;
`
const OptionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`
const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`
const LoadingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #4b5563;
    font-size: 0.95rem;
    padding: 20px 8px;
`
const LoadingIcon = styled(ImSpinner8)`
    width: 18px;
    height: 18px;
    animation: ${spin} 0.9s linear infinite;
`
const OptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

const OptionText = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 0.95rem;
    line-height: 1.35;
`;

const OptionWrapper = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    border: 1px solid;
    border-color: ${(props) => (props.checked ? "#007BFF" : "#ccc")};
    border-radius: 12px;
    background-color: ${(props) => (props.checked ? "#E0F0FF" : "#f9f9f9")};
    padding: 10px;
    & input {
        width: 20px;
        height: 20px;
        accent-color: ${(props) => (props.checked ? "#007BFF" : "#ccc")};

    }
    transition: all 0.3s;
    &:hover {
        border-color: #007BFF;
        background-color: #E0F0FF;
        & input {
            accent-color: #007BFF;
        }
    }
    &:hover ${LogoSlot} {
        border-color: #007BFF;
    }
`
const NavButton = styled.button`
    padding: 10px 25px;
    box-sizing: border-box;
    background-color: ${(props) => props.usable ? "#007BFF" : "#ccc"};
    color: #fff;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: background-color 0.3s;
    &:hover {
        background-color: ${(props) => props.usable ? "#0056b3" : "#ccc"};
    }
    margin-left: auto;
    margin-top: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: bold;
`
const IconeArrow = styled(BsArrowRight)`
    width: 22px;
    height: 22px;
    color: #fff;
`

export default function ControleMedicaoAmb() {
    const { ambiente, setAmbiente } = useAmbienteMedicao();
    const { openModal, isModalOpen, closeModal } = useModal();

    const selectedId = useMemo(() => ambiente?.id ?? null, [ambiente]);

    const api = useApi();

    const [ listaAmbientes, setListaAmbientes ] = useState([]);
    const [ loadingAmbientes, setLoadingAmbientes ] = useState(true);

    const requestAmbientes = async () => {
        setLoadingAmbientes(true);

        try{

            const { data } = await api.get("/Painel/API/ControleMedicao/AmbientesObra/");
            setListaAmbientes(data);

        }catch(e){

            console.error(e);

        }finally{

            setLoadingAmbientes(false);

        }

    }

    useEffect(() => {

        requestAmbientes();

    }, [])

    function handleAmbienteChange(id) {
        const a = listaAmbientes.find((x) => x.id === id) || null;
        setAmbiente(a);
    }

    const navigate = useNavigate();

    const openSidebar = () => {
        openModal("ambientesSidebar");
    }

    const openGerenciarObrasModal = () => {
        closeModal();
        openModal("gerenciarObrasMedicao");
    };

    const openNfPendentesModal = () => {
        closeModal();
        openModal("modalNfPendentesBm");
    }

    return (
        <>
            <Container>
                <NavBar changeState={openSidebar} />
                <SelectorWrapper>
                    <h2>SELECIONAR OBRA...</h2>
                    <Separator />
                    <OptionsWrapper>
                        {loadingAmbientes ? (
                            <LoadingWrapper>
                                <LoadingIcon />
                                Carregando obras...
                            </LoadingWrapper>
                        ) : (
                            listaAmbientes.map((a) => (
                                <OptionWrapper
                                    key={a.id}
                                    checked={selectedId === a.id}
                                >
                                    <ObraLogoThumb
                                        logoUrl={a.logo_url}
                                        active={selectedId === a.id}
                                    />
                                    <OptionRow>
                                        <input
                                            type="radio"
                                            name="ambiente"
                                            checked={selectedId === a.id}
                                            onChange={() => handleAmbienteChange(a.id)}
                                        />
                                        <OptionText>{a.descricao}</OptionText>
                                    </OptionRow>
                                </OptionWrapper>
                            ))
                        )}
                    </OptionsWrapper>
                    <NavButton
                        onClick={() => navigate("./pedidos-compra")}
                        usable={ambiente !== null && !loadingAmbientes}
                        disabled={ambiente === null || loadingAmbientes}
                    >
                        ACESSAR
                        <IconeArrow />
                    </NavButton>
                </SelectorWrapper>
            </Container>
            {
                isModalOpen("ambientesSidebar") && (
                    <Overlay state={true} changeContent={closeModal} >
                        <SideMenu>
                            <SideMenuBtn
                                button="GERENCIAR OBRAS"
                                clickFunc={ openGerenciarObrasModal }
                            />
                            <SideMenuBtn
                                button="NFs PENDENTES (BM)"
                                clickFunc={ openNfPendentesModal }
                            />
                        </SideMenu>
                    </Overlay>
                )
            }
            {
                isModalOpen("gerenciarObrasMedicao") && (
                    <ModalGerenciarObras
                        listaAmbientes={listaAmbientes}
                        onRefresh={requestAmbientes}
                        ambiente={ambiente}
                        setAmbiente={setAmbiente}
                    />
                )
            }
            {
                isModalOpen("novoAmbienteMedicao") && (
                    <ModalNovoAmbiente onCreated={requestAmbientes} />
                )
            }
            {
                isModalOpen("modalNfPendentesBm") && (
                    <ModalNfPendentesBM />
                )
            }
        </>
    );
}